import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, Target, Hash, ListChecks, CheckSquare, ArrowRight, Cog, CheckCircle2, Calendar as CalendarIcon } from 'lucide-react';
import { searchAll } from '../../lib/api';
import { AREE } from '../../constants/areas';
import { formatSettimanaConRange } from '../../lib/weekFormat';
import { useFocusTrap } from '../../lib/useFocusTrap';

/**
 * Command palette ⌘K (Ctrl+K su Windows/Linux).
 *
 * - Apri con ⌘K / Ctrl+K
 * - Chiudi con Esc o click fuori
 * - Frecce ↑/↓ per navigare, Enter per aprire l'item
 * - Ricerca su obiettivi, KR, azioni, MIT (backend /api/search)
 * - Anche shortcut rapidi a inizio lista: Oggi, Setup, Pianifica mese, Review settimana
 */
const DEBOUNCE_MS = 180;

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const debT = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Focus trap nel pannello. Niente onEscape (Esc è già gestito dal listener
  // globale) e initialFocus:false (resta l'autofocus esistente sull'input).
  useFocusTrap(open, panelRef, { initialFocus: false });

  // ⌘K shortcut globale
  useEffect(() => {
    const handler = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setQ('');
      setResults(null);
      setSelected(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    clearTimeout(debT.current);
    if (!q || q.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debT.current = setTimeout(() => {
      searchAll(q)
        .then((r) => { setResults(r); setSelected(0); })
        .catch(() => setResults({ obiettivi: [], key_results: [], azioni: [], mit: [] }))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(debT.current);
  }, [q, open]);

  // Costruisci lista flat di items navigabili
  const items = buildItems(q, results);

  const exec = (it) => {
    if (!it) return;
    setOpen(false);
    if (it.action) it.action(navigate);
    else if (it.to) navigate(it.to);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(items.length - 1, s + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(0, s - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      exec(items[selected]);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-start justify-center pt-24 px-4"
      style={{ background: 'rgb(var(--qp-ink-rgb) / 0.4)' }}
      onClick={() => setOpen(false)}
      data-testid="cmdk-overlay"
    >
      <div
        ref={panelRef}
        className="w-full max-w-2xl bg-canvas border border-line shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="cmdk-panel"
        role="dialog"
        aria-label="Cerca o naviga"
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-line">
          <Search size={18} strokeWidth={1.5} className="text-soft shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Cerca obiettivi, KR, azioni, MIT…  oppure scegli un'azione"
            className="flex-1 bg-transparent outline-none font-body text-base placeholder:text-mute"
            data-testid="cmdk-input"
            role="combobox"
            aria-expanded={open}
            aria-controls="cmdk-listbox"
            aria-activedescendant={items.length > 0 ? `cmdk-option-${selected}` : undefined}
          />
          <kbd className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute border border-line px-1.5 py-0.5">ESC</kbd>
          <button
            onClick={() => setOpen(false)}
            className="p-1 text-soft hover:text-ink"
            data-testid="cmdk-close"
            aria-label="Chiudi"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="cmdk-results" role="listbox" id="cmdk-listbox">
          {loading && <div className="px-5 py-4 font-mono text-xs text-mute">Cerco…</div>}
          {!loading && items.length === 0 && (
            <div className="px-5 py-8 font-body text-sm text-mute italic">
              {q.length >= 2 ? 'Nessun risultato.' : 'Scrivi almeno 2 caratteri.'}
            </div>
          )}
          {items.map((it, i) => (
            <ResultRow
              key={`${it.type}-${it.id || it.label}`}
              item={it}
              idx={i}
              active={i === selected}
              onClick={() => exec(it)}
              onHover={() => setSelected(i)}
            />
          ))}
        </div>

        {/* Footer hint */}
        <div className="border-t border-line px-5 py-2 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.15em] text-mute">
          <Hint k="↑↓">Naviga</Hint>
          <Hint k="↵">Apri</Hint>
          <Hint k="⌘K">Apri/Chiudi</Hint>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function buildItems(q, results) {
  const items = [];
  // Shortcut rapidi sempre primi quando query vuota o "vai"
  if (!q || q.length < 2) {
    items.push({ type: 'shortcut', icon: <CalendarIcon size={14} strokeWidth={1.5} />, label: 'Vai a Oggi', to: '/oggi' });
    items.push({ type: 'shortcut', icon: <Hash size={14} strokeWidth={1.5} />, label: 'Vai a Dashboard', to: '/' });
    items.push({ type: 'shortcut', icon: <CalendarIcon size={14} strokeWidth={1.5} />, label: 'Vai a Calendario', to: '/calendario' });
    items.push({ type: 'shortcut', icon: <Cog size={14} strokeWidth={1.5} />, label: 'Coach · Setup', to: '/setup' });
    items.push({ type: 'shortcut', icon: <Cog size={14} strokeWidth={1.5} />, label: 'Coach · Pianifica mese', to: '/mese' });
    items.push({ type: 'shortcut', icon: <CheckSquare size={14} strokeWidth={1.5} />, label: 'Coach · Review settimanale', to: '/settimana' });
    items.push({ type: 'shortcut', icon: <Hash size={14} strokeWidth={1.5} />, label: 'Stampa · PDF', to: '/stampa' });
    return items;
  }
  if (!results) return items;
  (results.obiettivi || []).forEach((o) => items.push({
    type: 'obiettivo',
    id: o.id,
    label: o.titolo,
    sub: `Obiettivo ${String(o.ordine).padStart(2, '0')} · ${o.area || '—'}`,
    area: o.area,
    to: `/obiettivo/${o.id}`,
  }));
  (results.key_results || []).forEach((k) => items.push({
    type: 'kr',
    id: k.id,
    label: k.titolo,
    sub: `Key Result · ${k.tipo}${k.obiettivo_titolo ? ` · ${k.obiettivo_titolo}` : ''}`,
    area: k.area,
    to: `/obiettivo/${k.obiettivo_id}`,
  }));
  (results.azioni || []).forEach((a) => items.push({
    type: 'azione',
    id: a.id,
    label: a.testo,
    sub: `Azione · ${a.settimana ? formatSettimanaConRange(a.settimana) : ''}${a.kr_titolo ? ` · ${a.kr_titolo}` : ''}`,
    area: a.area,
    done: a.fatto,
    to: a.settimana ? `/periodo/settimana/${a.settimana}` : '/',
  }));
  (results.mit || []).forEach((m) => items.push({
    type: 'mit',
    id: m.id,
    label: m.testo,
    sub: `Most Important Task · ${m.data || ''}${m.kr_titolo ? ` · ${m.kr_titolo}` : ''}`,
    done: m.fatto,
    to: m.data ? `/periodo/giorno/${m.data}` : '/',
  }));
  return items;
}

function ResultRow({ item, idx, active, onClick, onHover }) {
  const meta = item.area ? AREE[(item.area || '').toLowerCase()] : null;
  const icon = item.icon || ({
    obiettivo: <Target size={14} strokeWidth={1.5} />,
    kr: <Hash size={14} strokeWidth={1.5} />,
    azione: <ListChecks size={14} strokeWidth={1.5} />,
    mit: <CheckSquare size={14} strokeWidth={1.5} />,
    shortcut: <ArrowRight size={14} strokeWidth={1.5} />,
  }[item.type]);

  return (
    <button
      role="option"
      id={`cmdk-option-${idx}`}
      aria-selected={active}
      onClick={onClick}
      onMouseEnter={onHover}
      className={
        'w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ' +
        (active ? 'bg-linesoft' : 'hover:bg-linesoft/60')
      }
      data-testid={`cmdk-row-${item.type}-${item.id || item.label}`}
      style={{ borderLeft: `3px solid ${meta ? meta.color : 'transparent'}` }}
    >
      <span className="text-soft shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="font-body text-sm text-ink truncate flex items-center gap-2">
          {item.done && <CheckCircle2 size={12} strokeWidth={1.5} style={{ color: 'var(--qp-success)' }} />}
          <span className={item.done ? 'line-through text-mute' : ''}>{item.label}</span>
        </div>
        {item.sub && (
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute truncate">{item.sub}</div>
        )}
      </div>
      {active && <ArrowRight size={12} strokeWidth={1.5} className="text-soft shrink-0" />}
    </button>
  );
}

function Hint({ k, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <kbd className="border border-line px-1.5 py-0.5 text-soft">{k}</kbd> {children}
    </span>
  );
}
